'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHandsHelping,
  faBookOpen,
  faSeedling,
  faArrowRight,
  faHandHoldingHeart,
  faCalendarAlt,
  faClock,
  faMapMarkerAlt,
  faGraduationCap,
  faScaleBalanced,
  faShieldHalved
} from '@fortawesome/free-solid-svg-icons';

import styles from '@/styles/Home.module.css';
import { images } from '@/constants/images';
import TestimonialSlider from '@/components/common/TestimonialSlider';
import UpcomingEventsSection from '@/components/Programs/UpcomingEventsSection';
import DonationButton from '@/components/common/DonationButton';
import Dialog from '@/components/common/Dialog';
import ApplicationForm from '@/components/ApplicationForm/ApplicationForm';

export default function Home() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  const handleOpenForm = () => {
    setIsFormOpen(true);
  };
  
  const handleCloseForm = () => {
    setIsFormOpen(false);
  };
  
  const handleFormSuccess = () => {
    setIsFormOpen(false);
  };

  return (
    <>
      <section className={styles.hero}>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <h1>Removing Barriers to Success - Making an Impact</h1>
          <p>Join TimeLee Cares in our mission to provide Comprehensive Access to Resources, Education and pathway Support</p>
          <div className={styles.heroCta}>
            <button 
              onClick={handleOpenForm} 
              className="btn btn--primary" 
              title="Register for TimeLee Cares Foundation - Get help and support for students"
              aria-label="Open application form for TimeLee Cares support"
            >
              Get Help & Support
            </button>
            <Link 
              href="/donate" 
              className="btn btn--secondary" 
              aria-label="Navigate to donation page" 
              title="Make a donation to support TimeLee Cares Foundation"
            >
              Donate Now
            </Link>
            <Link 
              href="/programs" 
              className="btn btn--secondary" 
              aria-label="View all programs and services" 
              title="Browse our programs and services"
            >
              Our Programs
            </Link>
            
            <Dialog 
              isOpen={isFormOpen} 
              onClose={handleCloseForm}
              title="Apply for TimeLee Cares Foundation Support"
              size="large"
              showCloseButton={false}
            >
              <ApplicationForm 
                onSuccess={handleFormSuccess}
                onCancel={handleCloseForm}
                variant="modal"
                showTitle={false}
                buttonText="Submit Application"
              />
            </Dialog>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className={styles.mission}>
        <div className={styles.container}>
          <h2>Our Mission</h2>
          <p>
            TimeLee Cares is dedicated to improving the lives of those in need through 
            compassionate service, education, and community development. We believe in 
            creating sustainable solutions that empower individuals and strengthen communities.
          </p>
        </div>
      </section>

      {/* Featured Programs */}
      <section className={styles.featuredPrograms}>
        <div className={styles.container}>
          <h2>Our Impact</h2>
          <div className={styles.programGrid}>
            <div className={styles.programCard}>
              <div className={styles.programIcon}>
                <FontAwesomeIcon icon={faHandsHelping} aria-hidden="true" />
              </div>
              <h3>Community Outreach</h3>
              <p>Providing essential services to underserved communities.</p>
            </div>
            <div className={styles.programCard}>
              <div className={styles.programIcon}>
                <FontAwesomeIcon icon={faBookOpen} aria-hidden="true" />
              </div>
              <h3>Education Initiatives</h3>
              <p>Supporting educational opportunities for all ages.</p>
            </div>
            <div className={styles.programCard}>
              <div className={styles.programIcon}>
                <FontAwesomeIcon icon={faSeedling} aria-hidden="true" />
              </div>
              <h3>Pathway Support</h3>
              <p>Working to enhance youth and young adults to achieve success in their pathway.</p>
            </div>
          </div>
          <div className={styles.programsLink}>
            <Link 
              href="/programs" 
              className={styles.textLink} 
              aria-label="View all programs and services" 
              title="Browse our complete list of programs and services"
            >
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              <span className="sr-only">View all programs</span>
            </Link>
          </div>
        </div>
      </section>

      {/* 2026-2027 College Planning */}
      <section className={styles.featuredPrograms}>
        <div className={styles.container}>
          <h2>2026-2027 College Planning</h2>
          <div className={styles.programGrid}>
            <div className={styles.programCard}>
              <div className={styles.programIcon}>
                <FontAwesomeIcon icon={faCalendarAlt} aria-hidden="true" />
              </div>
              <h3>Sessions</h3>
              <p>Upcoming and past sessions for academic and career guidance.</p>
              <Link href="/sessions" className={styles.textLink}>
                View Sessions <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
            </div>
            <div className={styles.programCard}>
              <div className={styles.programIcon}>
                <FontAwesomeIcon icon={faGraduationCap} aria-hidden="true" />
              </div>
              <h3>Timeline</h3>
              <p>Month-by-month roadmap from application launch to decision day.</p>
              <Link href="/timeline" className={styles.textLink}>
                View Timeline <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
            </div>
            <div className={styles.programCard}>
              <div className={styles.programIcon}>
                <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
              </div>
              <h3>EA vs ED</h3>
              <p>Compare Early Action and Early Decision to find your path.</p>
              <Link href="/ea-vs-ed" className={styles.textLink}>
                Compare <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className={styles.testimonials}>
        <div className={styles.container}>
          <h2>Voices from Our Community</h2>
          <TestimonialSlider />
        </div>
      </section>


      {/* Next Upcoming Event - Hero Style */}
      <UpcomingEventsSection maxEvents={1} showTitle={false} variant="hero" />

      {/* UNCF Webinar Series Section */}

      {/* Call to Action */}
      <section className={styles.cta}>
        <div className={styles.container}>
          <h2>Join Our Mission</h2>
          <p>Together, we can make a lasting impact in our community.</p>
          <div className={styles.ctaButtons}>
            <button 
              onClick={handleOpenForm} 
              className="btn btn--primary" 
              title="Register for TimeLee Cares Foundation - Get help and support for students"
              aria-label="Open application form for TimeLee Cares support"
            >
              Get Help & Support
            </button>
            <Link 
              href="/donate" 
              className="btn btn--secondary" 
              aria-label="Navigate to donation page" 
              title="Make a donation to support TimeLee Cares Foundation"
            >
              Donate
            </Link>
            <Link 
              href="/contact" 
              className="btn btn--secondary" 
              aria-label="Contact us to volunteer" 
              title="Get in touch about volunteer opportunities"
            >
              Volunteer
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
